import _ from 'lodash';
import { uniq } from 'lodash';
import ReactTable from 'react-table';
import React, { Component } from 'react';
import { reduxForm, Field } from 'redux-form';
import { Form, Grid, Responsive, Checkbox } from 'semantic-ui-react';

import { TitledCard } from 'Components/';
import { SelectSitesTableReduxForm } from 'Common/SelectSitesTableReduxForm';
import { caseInsensitiveFilter } from 'Common/GridHelper';
import { UniversalDataExportEnum as Fields } from 'Common/FieldEnum';
import { InputField, SelectField } from 'Common/SemanticWrapper';
import { UNIVERSAL_DATA_EXPORT_FORM_NAME } from 'Common/FormEnum';
import { IMPORT_TYPE_MAP, IMPORT_TYPE_OPTIONS } from 'Common/Constants';
import { ReduxFormButtonGroup } from 'Components/forms/ReduxFormButtonGroup';

import {
  GroupStatusOptions,
  GROUPS,
  GroupStatusLabels,
} from '../helpers/constants';
import { convertStatusToInt } from '../helpers';

class UniversalDataExportForm extends Component {
  // ====== Settings =======

  // hide filters if using previous configuration without having selected a configuration
  get showFilters() {
    const { formValues } = this.props;
    return !(
      _.get(formValues, Fields.ExportType) ===
        IMPORT_TYPE_MAP.LOAD_SAVED.value && !_.get(formValues, Fields.ConfigId)
    );
  }

  get savedConfig() {
    const { configsMap, formValues } = this.props;
    const config = _.get(configsMap, formValues[Fields.ConfigId]);
    return config;
  }

  renderImportInput() {
    const {
      fields,
      formValues,
      configsMap,
      configOptions,
      change,
    } = this.props;

    const exportType = formValues[Fields.ExportType];

    let inputProps = {};

    switch (exportType) {
      case IMPORT_TYPE_MAP.CREATE_NEW.value:
        inputProps = {
          component: InputField,
          label: 'Name of configuration to be saved',
          name: Fields.ConfigName,
          hideEmptyError: true,
        };
        break;
      case IMPORT_TYPE_MAP.LOAD_SAVED.value:
        inputProps = {
          component: SelectField,
          label: 'Select a saved configuration',
          options: configOptions,
          name: Fields.ConfigId,
          onChange: (e, id) => {
            const savedConfig = _.get(configsMap, id);
            if (!savedConfig) {
              return;
            }
            const fieldId = _.get(savedConfig, [Fields.SelectedFieldIds, 0]);
            if (fieldId) {
              const field = _.find(fields, f => f[Fields.Id] === fieldId);
              if (field) {
                change(Fields.GroupId, field[Fields.GroupId]);
              }
            }
            change(Fields.SelectedSites, savedConfig[Fields.SelectedSites]);
            change(
              Fields.Status,
              convertStatusToInt(savedConfig[Fields.Status])
            );
            change(Fields.ConfigId, savedConfig[Fields.ConfigId]);
            change(
              Fields.SelectedFieldIds,
              savedConfig[Fields.SelectedFieldIds]
            );
          },
        };
        break;
      default:
        return null;
    }

    return (
      <Grid.Row>
        <Grid.Column>
          <Field {...inputProps} />
        </Grid.Column>
      </Grid.Row>
    );
  }

  // ====== Settings =======

  // ====== Field Picker ======

  selectFields(fieldIds = [], checked = false) {
    const { formValues, change } = this.props;
    const selectedFieldIds = formValues[Fields.SelectedFieldIds];
    let newSelectedFieldIds;

    if (checked) {
      newSelectedFieldIds = uniq([...selectedFieldIds, ...fieldIds]);
    } else {
      newSelectedFieldIds = selectedFieldIds.filter(
        id => !fieldIds.includes(id)
      );
    }

    change(Fields.SelectedFieldIds, newSelectedFieldIds);
  }

  getTableProps() {
    const { fieldsByGroupId, formValues } = this.props;
    const selectedFieldIds = formValues[Fields.SelectedFieldIds] || [];

    const props = {
      data: fieldsByGroupId,
      showPagination: true,
      collapseOnDataChange: false,
      collapseOnSortingChange: false,
      defaultPageSize: 5,
      columns: [
        {
          Header: 'Categories',
          headerStyle: {
            textAlign: 'left',
          },
          width: 150,
          id: 'CategoryName',
          accessor: d => d[Fields.CategoryName],
          aggregate: (vals, rows) => vals[0],
        },
        {
          Header: 'Fields',
          headerStyle: {
            textAlign: 'left',
          },
          id: 'Id',
          accessor: d => d.Id,
          Aggregated: row => {
            const fields = row.subRows;
            const fieldIdsInFieldGroup = fields.map(f => f.Id);
            let fieldCheckedCount = fieldIdsInFieldGroup.reduce((acc, id) => {
              if (selectedFieldIds.includes(id)) {
                acc++;
              }
              return acc;
            }, 0);

            const allChecked =
              fieldCheckedCount === fieldIdsInFieldGroup.length;
            const indeterminate = !allChecked && !!fieldCheckedCount;
            return (
              <Checkbox
                label="All"
                checked={allChecked}
                indeterminate={indeterminate}
                onChange={(e, data) => {
                  this.selectFields(fieldIdsInFieldGroup, data.checked);
                }}
              />
            );
          },
          Cell: ({ row, original }) => {
            if (original) {
              return (
                <div style={{ paddingLeft: 20 }}>
                  <Checkbox
                    label={original[Fields.Description]}
                    checked={selectedFieldIds.includes(original.Id)}
                    onChange={(e, data) => {
                      this.selectFields([original.Id], data.checked);
                    }}
                  />
                </div>
              );
            }
          },
        },
      ],
      pivotBy: [Fields.CategoryName],
    };

    return props;
  }

  renderFiltersForGroup() {
    const { savedConfig } = this;
    const { formValues } = this.props;

    const groupId = formValues[Fields.GroupId];
    const shouldRenderSitesTable = groupId === GROUPS.Student;
    const initialSelectedSiteIds = _.get(savedConfig, Fields.SelectedSites, []);

    return (
      <React.Fragment>
        <ReduxFormButtonGroup
          label={_.get(GroupStatusLabels, groupId)}
          options={_.get(GroupStatusOptions, groupId, [])}
          formName={UNIVERSAL_DATA_EXPORT_FORM_NAME}
          fieldName={Fields.Status}
        />
        <br />
        {shouldRenderSitesTable && (
          <Field
            enableReinitialize
            initialSelectedSiteIds={initialSelectedSiteIds}
            name={Fields.SelectedSites}
            component={SelectSitesTableReduxForm}
          />
        )}
      </React.Fragment>
    );
  }

  renderFilters() {
    const { showFilters } = this;

    return (
      <TitledCard collapsible title="Filters">
        {showFilters ? (
          this.renderFiltersForGroup()
        ) : (
          <p style={{ color: '#a2a6ab' }}>
            Please select a configuration option.
          </p>
        )}
      </TitledCard>
    );
  }

  renderFieldPicker = () => {
    const { showFilters, savedConfig } = this;
    const { change, groupOptions } = this.props;

    return (
      <TitledCard title="Field Picker">
        {showFilters ? (
          <React.Fragment>
            <Field
              name={Fields.GroupId}
              label={'Group'}
              component={SelectField}
              options={groupOptions}
              onChange={value => {
                const fieldIds = _.get(
                  savedConfig,
                  Fields.SelectedFieldIds,
                  []
                );

                change(Fields.SelectedFieldIds, fieldIds);
              }}
            />
            <ReactTable
              loading={false}
              {...this.getTableProps()}
              defaultFilterMethod={caseInsensitiveFilter}
            />
          </React.Fragment>
        ) : (
          <p style={{ color: '#a2a6ab' }}>
            Please select a configuration option.
          </p>
        )}
      </TitledCard>
    );
  };

  renderExportSettings = () => {
    const { savedConfig } = this.props;
    const { change } = this.props;

    return (
      <TitledCard title="Export Settings">
        <ReduxFormButtonGroup
          label="Export Type"
          options={IMPORT_TYPE_OPTIONS}
          formName={UNIVERSAL_DATA_EXPORT_FORM_NAME}
          fieldName={Fields.ExportType}
          onChange={exportType => {
            if (exportType === IMPORT_TYPE_MAP.LOAD_SAVED.value) {
              change(
                Fields.SelectedFieldIds,
                _.get(savedConfig, Fields.SelectedFieldIds, [])
              );
              change(
                Fields.SelectedSites,
                _.get(savedConfig, Fields.SelectedSites, [])
              );
              change(Fields.Status, _.get(savedConfig, Fields.Status, 0));
            }
          }}
        />
        {this.renderImportInput()}
      </TitledCard>
    );
  };

  render() {
    return (
      <Form>
        <Responsive as={Grid} columns={2} padded minWidth={1475}>
          <Grid.Column>
            {this.renderFieldPicker()}
            {this.renderFilters()}
          </Grid.Column>
          <Grid.Column>{this.renderExportSettings()}</Grid.Column>
        </Responsive>
        <Responsive as={Grid} padded maxWidth={1474}>
          <Grid.Column>
            {this.renderExportSettings()}
            {this.renderFieldPicker()}
            {this.renderFilters()}
          </Grid.Column>
        </Responsive>
      </Form>
    );
  }
}

UniversalDataExportForm = reduxForm({
  form: UNIVERSAL_DATA_EXPORT_FORM_NAME,
})(UniversalDataExportForm);

export { UniversalDataExportForm };



// WEBPACK FOOTER //
// ./src/pages/universaldataexport/components/UniversalDataExportForm.js